import React, { useEffect, useState } from 'react';
import { Sparkles, X, Calendar, TrendingUp, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import ShareableGratitudeCard from './ShareableGratitudeCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDayLabel = (isoDate, todayIso) => {
  if (isoDate === todayIso) return 'Today';
  const d = new Date(isoDate + 'T00:00:00');
  const today = new Date(todayIso + 'T00:00:00');
  const diffMs = today - d;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return 'Yesterday';
  return DAY_NAMES[d.getDay()];
};

const formatDateShort = (isoDate) => {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const GratitudeWeekDigest = ({ isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareCard, setShowShareCard] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/reflections/gratitude/week`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const todayIso = data?.week_end;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm animate-fade-in px-4 py-6"
      data-testid="gratitude-week-digest-modal"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-[#252528] via-[#252528] to-[#1F1F22] border border-[#C6A75E]/30 rounded-[28px] w-full max-w-md mx-auto max-h-[90vh] flex flex-col relative overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-24 -right-24 w-56 h-56 bg-[#C6A75E]/15 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-56 h-56 bg-[#8BAA9E]/10 rounded-full blur-[80px] pointer-events-none" />

        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
          <div>
            <p className="text-[#C6A75E] text-[10px] tracking-[0.25em] uppercase font-sans mb-1">
              ✨ Your Week in Gratitude
            </p>
            <h2 className="font-serif text-2xl text-[#DCD3CA]">Seven days of joy</h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#7A7670] hover:text-[#DCD3CA] p-1"
            data-testid="close-gratitude-week-digest"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Stats strip */}
        {data && !loading && (
          <div className="relative px-6 pb-4 grid grid-cols-3 gap-3">
            <div className="bg-[#1C1C1E]/80 rounded-xl p-3 border border-white/5">
              <div className="flex items-center gap-1.5 text-[#8BAA9E] mb-1">
                <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-[10px] font-sans uppercase tracking-wider">Days</span>
              </div>
              <p className="font-serif text-xl text-[#DCD3CA]">{data.days_completed}<span className="text-[#7A7670] text-sm">/7</span></p>
            </div>
            <div className="bg-[#1C1C1E]/80 rounded-xl p-3 border border-white/5">
              <div className="flex items-center gap-1.5 text-[#C6A75E] mb-1">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-[10px] font-sans uppercase tracking-wider">Entries</span>
              </div>
              <p className="font-serif text-xl text-[#DCD3CA]">{data.total_entries}</p>
            </div>
            <div className="bg-[#1C1C1E]/80 rounded-xl p-3 border border-white/5">
              <div className="flex items-center gap-1.5 text-[#8BAA9E] mb-1">
                <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-[10px] font-sans uppercase tracking-wider">Rate</span>
              </div>
              <p className="font-serif text-xl text-[#DCD3CA]">{data.completion_rate}<span className="text-[#7A7670] text-sm">%</span></p>
            </div>
          </div>
        )}

        {/* Days list */}
        <div className="relative flex-1 overflow-y-auto px-6 pb-6 space-y-3">
          {loading && (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#C6A75E] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-[#7A7670] text-sm font-sans mt-3">Gathering your week...</p>
            </div>
          )}

          {!loading && data && data.total_entries === 0 && (
            <div className="text-center py-12">
              <Sparkles className="w-10 h-10 text-[#C6A75E]/40 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#DCD3CA] font-serif text-base mb-2">Your week awaits</p>
              <p className="text-[#7A7670] text-sm font-sans">Start today — small practice, lasting impact.</p>
            </div>
          )}

          {!loading && data && data.days.slice().reverse().map((day) => {
            const isToday = day.date === todayIso;
            const hasEntries = day.entries.length > 0;
            return (
              <div
                key={day.date}
                data-testid={`week-day-${day.date}`}
                className={`rounded-2xl p-4 border transition-all ${
                  hasEntries
                    ? 'bg-[#1C1C1E]/60 border-[#C6A75E]/20'
                    : 'bg-[#1C1C1E]/30 border-white/5'
                }`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <span className={`font-serif text-base ${hasEntries ? 'text-[#DCD3CA]' : 'text-[#7A7670]'}`}>
                    {formatDayLabel(day.date, todayIso)}
                    {isToday && (
                      <span className="ml-2 text-[#C6A75E] text-[10px] tracking-wider uppercase font-sans">Today</span>
                    )}
                  </span>
                  <span className="text-[#7A7670] text-xs font-sans">{formatDateShort(day.date)}</span>
                </div>
                {hasEntries ? (
                  <ul className="space-y-1.5">
                    {day.entries.map((entry, i) => (
                      <li key={`${day.date}-${i}-${entry.slice(0,6)}`} className="text-[#DCD3CA] text-sm font-sans flex gap-2 leading-relaxed">
                        <span className="text-[#C6A75E] flex-shrink-0">·</span>
                        <span>{entry}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#7A7670] text-xs font-sans italic">— no practice recorded —</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {!loading && data && data.total_entries > 0 && (
          <div className="relative px-6 pb-6 pt-2 border-t border-white/5">
            <p className="text-[#B5B1AB] text-xs font-sans italic text-center leading-relaxed">
              &ldquo;Small practice. Lasting impact.&rdquo;
            </p>
          </div>
        )}

        {/* Close button (mobile-friendly) */}
        <div className="relative px-6 pb-6 flex gap-2">
          {!loading && data && data.total_entries > 0 && (
            <Button
              onClick={() => setShowShareCard(true)}
              className="flex-1 bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full py-3 font-medium flex items-center justify-center gap-2"
              data-testid="open-shareable-gratitude-btn"
            >
              <Share2 className="w-4 h-4" strokeWidth={2} />
              Share my week
            </Button>
          )}
          <Button
            onClick={onClose}
            variant={!loading && data && data.total_entries > 0 ? "outline" : "default"}
            className={
              !loading && data && data.total_entries > 0
                ? "flex-1 border-white/15 text-[#DCD3CA] hover:bg-white/5 rounded-full py-3"
                : "w-full bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full py-3 font-medium"
            }
            data-testid="close-gratitude-week-btn"
          >
            Close
          </Button>
        </div>
      </div>

      {/* Shareable card overlay (rendered above this modal) */}
      <ShareableGratitudeCard
        isOpen={showShareCard}
        onClose={() => setShowShareCard(false)}
        weekData={data}
      />
    </div>
  );
};

export default GratitudeWeekDigest;
